import { useMemo } from "react";

import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import categoriesService from "../services/categories-service";
import type {
    EnrichedCategorizedGroup,
    FilterCategory,
    IFilterCategoryWithCount,
    IFiltersConfigCategoryItem,
} from "../types/category";
import type { IFilterInstance } from "../types/filterInstance";

type Params<T extends IPlainObject> = {
    readonly visibleFilters: readonly IFilterInstance<T>[];
    readonly fullFiltersConfig: readonly IFilterInstance<T>[];
    readonly categories: readonly IFiltersConfigCategoryItem[];
    readonly categoriesWithHiddenFilters: ReadonlySet<FilterCategory>;
    readonly isSearchActive: boolean;
};

type ViewModel<T extends IPlainObject> = {
    readonly permanentFilters: readonly IFilterInstance<T>[];
    readonly groups: readonly EnrichedCategorizedGroup<IFilterInstance<T>>[];
    readonly categoriesWithCounts: readonly IFilterCategoryWithCount[];
};

export const useCategorizedFiltersViewModel = <T extends IPlainObject>({
    visibleFilters,
    fullFiltersConfig,
    categories,
    categoriesWithHiddenFilters,
    isSearchActive,
}: Params<T>): ViewModel<T> => {
    const { permanentFilters, groups } = useMemo(() => {
        const filtersByCategory = categoriesService.groupFiltersByCategory(visibleFilters, categories, isSearchActive);

        return {
            permanentFilters: filtersByCategory.permanentFilters,
            groups: filtersByCategory.groups.map((group) => ({
                ...group,
                hasHiddenFilters: categoriesWithHiddenFilters.has(group.category.id),
            })),
        };
    }, [visibleFilters, categories, categoriesWithHiddenFilters, isSearchActive]);

    const categoriesWithCounts = useMemo(
        () => categoriesService.computeCategoryCounts(fullFiltersConfig, categories),
        [fullFiltersConfig, categories],
    );

    return { permanentFilters, groups, categoriesWithCounts };
};
